import { Box, Chip, Typography } from "@mui/material";
import { FilterList, ViewKanban } from "@mui/icons-material";
import InfoHeader from "../components/InfoHeader";
import CardDesdobramento from "../components/CardDesdobramento";
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { useCallback, useEffect, useState } from "react";
import SelectAcao from "../components/SelectAcao";
import PlanoAcao from "../types/negocio/PlanoAcao";
import { Desdobramento } from "../types/negocio/Desdobramento";
import { desdobramentoService } from "../services/DesdobramentoService";
import toastService from "../utils/toastService";
import { planoAcaoService } from "../services/PlanoAcaoService";
import { PrimaryButton, ConfirmDeleteDialog } from "../components";
import SelectColaborador from "../components/SelectColaborador";
import TarefaModal from "../components/modais/TarefaModal";
import RetornoFinanceiroModal from "../components/modais/RetornoFinanceiroModal";
import LoadingIndicator from "../components/LoadingIndicator";

export default function ThinkBoard(props: Readonly<{ planoAcaoGuid: string }>) {
	const [tasks, setTasks] = useState<{ [key: string]: Desdobramento[] }>({
		pendente: [],
		emAndamento: [],
		finalizada: []
	});
	const [selectedAcao, setSelectedAcao] = useState<PlanoAcao | undefined>();
	const [loadingCards, setLoadingCards] = useState<{ [key: string]: boolean }>({});
	const [loadingBoard, setLoadingBoard] = useState(false);
	const [responsavel, setResponsavel] = useState('');

	// Modal states
	const [modalAberto, setModalAberto] = useState(false);
	const [modoEdicao, setModoEdicao] = useState(false);
	const [tarefaSelecionada, setTarefaSelecionada] = useState<Desdobramento>({} as Desdobramento);

	// Modal de retorno financeiro
	const [retornoFinanceiroModalAberto, setRetornoFinanceiroModalAberto] = useState(false);
	const [planoFinalizadoGuid, setPlanoFinalizadoGuid] = useState<string>('');

	// Mapping between column IDs and situacaoKanban values
	const columnToSituacao: Record<string, string> = {
		pendente: 'Pendente',
		emAndamento: 'Em Andamento',
		finalizada: 'Finalizada'
	};

	const criarTarefaVazia = (): Desdobramento => ({
		guid: '',
		planoAcaoGuid: selectedAcao?.guid || "",
		oque: "",
		porQue: "",
		quem: "",
		quemNomeCompleto: "",
		como: "",
		quando: "",
		onde: "",
		quantoCusta: "",
		situacaoKanban: "Pendente",
	});

	useEffect(() => {
		if (props.planoAcaoGuid) {
			planoAcaoService.buscarPorId(props.planoAcaoGuid)
				.then((acao) => {
					if (acao) {
						setSelectedAcao(acao);
					} else {
						toastService.error("Ação não encontrada");
					}
				})
				.catch(error => {
					console.error("Erro ao buscar ação:", error);
					toastService.error("Erro ao carregar ação");
				});
		}
	}, [props.planoAcaoGuid]);

	const parseDateBR = (dateStr: string): number => {
		if (!dateStr) return Infinity;
		const [day, month, year] = dateStr.split('/');
		return new Date(`${year}-${month}-${day}`).getTime();
	};

	const sortByDate = (tarefas: Desdobramento[]): Desdobramento[] =>
		[...tarefas].sort((a, b) => parseDateBR(a.quando) - parseDateBR(b.quando));

	const distribuirTarefas = useCallback((data: Desdobramento[]) => {
		const updatedTasks: { [key: string]: Desdobramento[] } = {
			pendente: [],
			emAndamento: [],
			finalizada: []
		};
		data.forEach((desdobramento) => {
			if (desdobramento.situacaoKanban === 'Pendente') {
				updatedTasks.pendente.push(desdobramento);
			} else if (desdobramento.situacaoKanban === 'Em Andamento') {
				updatedTasks.emAndamento.push(desdobramento);
			} else if (desdobramento.situacaoKanban === 'Finalizada') {
				updatedTasks.finalizada.push(desdobramento);
			} else {
				updatedTasks.pendente.push({ ...desdobramento, situacaoKanban: 'Pendente' });
			}
		});
		updatedTasks.pendente = sortByDate(updatedTasks.pendente);
		updatedTasks.emAndamento = sortByDate(updatedTasks.emAndamento);
		updatedTasks.finalizada = sortByDate(updatedTasks.finalizada);
		setTasks(updatedTasks);
	}, []);

	const buscarDesdobramentos = useCallback(async () => {
		try {
			setLoadingBoard(true);
			const data = await desdobramentoService.buscarComFiltros({
				planoAcaoGuid: selectedAcao?.guid || undefined,
				responsavel: responsavel || undefined,
			});
			distribuirTarefas(data);
		} catch (error) {
			console.error("Erro ao carregar tarefas:", error);
			toastService.error("Erro ao carregar tarefas");
		} finally {
			setLoadingBoard(false);
		}
	}, [selectedAcao?.guid, responsavel, distribuirTarefas]);

	// Carregar todas as tarefas ao montar o componente
	useEffect(() => {
		if (!props.planoAcaoGuid) {
			buscarDesdobramentos();
		}
	}, []);

	// Recarregar quando a ação selecionada mudar (inclui quando vem via props)
	useEffect(() => {
		if (selectedAcao?.guid) {
			buscarDesdobramentos();
		}
	}, [selectedAcao]);

	const handleEdit = (taskGuid: string) => {
		// Find the task in all columns
		let foundTask: Desdobramento | undefined;
		Object.values(tasks).forEach(columnTasks => {
			const task = columnTasks.find(t => t.guid === taskGuid);
			if (task) foundTask = task;
		});

		if (foundTask) {
			setTarefaSelecionada(foundTask);
			setModoEdicao(true);
			setModalAberto(true);
		}
	};

	const handleViewMore = (taskGuid: string) => {
		// Find the task in all columns
		let foundTask: Desdobramento | undefined;
		Object.values(tasks).forEach(columnTasks => {
			const task = columnTasks.find(t => t.guid === taskGuid);
			if (task) foundTask = task;
		});

		if (foundTask) {
			setTarefaSelecionada(foundTask);
			setModoEdicao(false);
			setModalAberto(true);
		}
	};

	const handleSaveTarefa = async (data: Desdobramento) => {
		try {
			const isNovaTarefa = !data.guid || data.guid.trim() === '';
			
			if (isNovaTarefa) {
				await desdobramentoService.criar(data);
				toastService.success('Tarefa criada com sucesso!');
			} else {
				await desdobramentoService.atualizar(data);
				toastService.success('Tarefa atualizada com sucesso!');
			}
			
			setModalAberto(false);
			
			// Atualizar a lista recarregando as tarefas
			await buscarDesdobramentos();
		} catch (error) {
			console.error('Erro ao salvar tarefa:', error);
			toastService.error('Erro ao salvar tarefa.');
		}
	};

	const onDragEnd = async (result: DropResult) => {
		const { source, destination } = result;
		if (!destination || source.droppableId === destination.droppableId) return;

		// Get the task being moved
		const taskToMove = tasks[source.droppableId as keyof typeof tasks][source.index];

		// Set loading state for this card
		setLoadingCards(prev => ({ ...prev, [taskToMove.guid]: true }));

		// Create new arrays for both source and destination columns
		const sourceTasks = Array.from(tasks[source.droppableId as keyof typeof tasks]);
		const [removed] = sourceTasks.splice(source.index, 1);
		const destinationTasks = Array.from(tasks[destination.droppableId as keyof typeof tasks]);
		destinationTasks.splice(destination.index, 0, removed);

		// Update the task situacaoKanban
		const novaSituacao = columnToSituacao[destination.droppableId];
		const taskToUpdate = { ...removed, situacaoKanban: novaSituacao };

		// Optimistically update UI
		setTasks({
			...tasks,
			[source.droppableId]: sourceTasks,
			[destination.droppableId]: destinationTasks,
		});

		// Update in backend
		try {
			const resultado = await desdobramentoService.atualizarSituacaoKanban(taskToUpdate.guid, novaSituacao);
			toastService.success(`Situação atualizada para: ${novaSituacao}`);
			
			// Se o plano foi finalizado, abrir modal de retorno financeiro
			if (resultado.planoFinalizado && taskToUpdate.planoAcaoGuid) {
				setPlanoFinalizadoGuid(taskToUpdate.planoAcaoGuid);
				setRetornoFinanceiroModalAberto(true);
			}
		} catch (error) {
			console.error("Erro ao atualizar situação do kanban:", error);
			toastService.error("Erro ao atualizar situação do tarefas");

			// Revert the UI change on error
			sourceTasks.splice(source.index, 0, removed);
			destinationTasks.splice(destination.index, 1);

			setTasks({
				...tasks,
				[source.droppableId]: sourceTasks,
				[destination.droppableId]: destinationTasks,
			});
		} finally {
			// Clear loading state
			setLoadingCards(prev => ({ ...prev, [taskToMove.guid]: false }));
		}
	};

	// State for confirmation dialog
	const [deleteConfirmation, setDeleteConfirmation] = useState<{
		isOpen: boolean;
		columnId: string;
		taskId: string;
		taskName: string;
	}>({
		isOpen: false,
		columnId: '',
		taskId: '',
		taskName: ''
	});

	const confirmDelete = (columnId: string, taskId: string, taskName: string) => {
		setDeleteConfirmation({
			isOpen: true,
			columnId,
			taskId,
			taskName
		});
	};

	const handleDeleteConfirmed = async () => {
		const { columnId, taskId } = deleteConfirmation;

		try {
			// Set loading state for this card
			setLoadingCards(prev => ({ ...prev, [taskId]: true }));

			await desdobramentoService.deletar(taskId);

			const updatedTasks = {
				...tasks,
				[columnId]: tasks[columnId as keyof typeof tasks].filter((task) => task.guid !== taskId),
			};
			setTasks(updatedTasks);

			toastService.success("Tarefa excluído com sucesso");
		} catch (error) {
			console.error("Erro ao excluir tarefa:", error);
			toastService.error("Erro ao excluir tarefa");
		} finally {
			// Clear loading state
			setLoadingCards(prev => ({ ...prev, [taskId]: false }));
			// Close the confirmation dialog
			setDeleteConfirmation(prev => ({ ...prev, isOpen: false }));
		}
	};

	const handleCancelDelete = () => {
		setDeleteConfirmation(prev => ({ ...prev, isOpen: false }));
	};

	// Replace the old handleDelete function with this one that shows the confirmation dialog
	const handleDelete = (columnId: string, taskId: string) => {
		const task = tasks[columnId as keyof typeof tasks].find(t => t.guid === taskId);
		if (task) {
			confirmDelete(columnId, taskId, task.oque || 'esta tarefa');
		}
	};

	// Handler para salvar retorno financeiro
	const handleRetornoFinanceiroSave = async (retornoFinanceiro: string) => {
		try {
			if (planoFinalizadoGuid) {
				const plano = await planoAcaoService.buscarPorId(planoFinalizadoGuid);
				const planoAtualizado = { ...plano, retornoFinanceiro };
				await planoAcaoService.atualizar(planoAtualizado);
				toastService.success('Retorno financeiro registrado com sucesso!');
			}
		} catch (error) {
			console.error('Erro ao salvar retorno financeiro:', error);
			toastService.error('Erro ao salvar retorno financeiro.');
		} finally {
			setRetornoFinanceiroModalAberto(false);
			setPlanoFinalizadoGuid('');
		}
	};

	const totalTarefas = tasks.pendente.length + tasks.emAndamento.length + tasks.finalizada.length;

	const columnConfig = [
		{ id: 'pendente', title: 'Pendentes', color: '#EF443C' },
		{ id: 'emAndamento', title: 'Em Andamento', color: '#042E4B' },
		{ id: 'finalizada', title: 'Finalizadas', color: '#2E7D32' },
	];

	return (
		<Box>
			<InfoHeader
				title="Think Board"
				message="Visualize e gerencie suas tarefas em formato Kanban. Arraste os cards entre as colunas para atualizar o status. Use os filtros para refinar a visualização."
				isImportant
				url='https://youtu.be/GWZ9lVTprWQ'
				modalTitle='Entenda a aba Tarefas'
			/>

			{/* Filtros */}
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
			<Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
					<SelectAcao
						sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: '250px' } }}
						value={selectedAcao ?? null}
						onChange={(newValue) => setSelectedAcao(newValue)}
						label="Buscar por Ação"
					/>

					<Box sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: '250px' } }}>
						<SelectColaborador
							value={responsavel}
							onChange={(colaborador) => setResponsavel(colaborador?.guid ?? '')}
						/>
					</Box>

					<PrimaryButton
						variant="contained"
						color="primary"
						onClick={buscarDesdobramentos}
						loading={loadingBoard}
						sx={{ minWidth: { xs: '100%', sm: '120px' }, height: '56px' }}
					>
						Filtrar
					</PrimaryButton>
				</Box>
			</Box>

			{/* Resumo do board */}
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
				<ViewKanban fontSize="small" color="primary" />
				<Typography variant="subtitle2" color="text.secondary">
					{totalTarefas} {totalTarefas === 1 ? 'tarefa' : 'tarefas'} no board
				</Typography>
			</Box>

			{/* Board Kanban */}
			{loadingBoard ? (
				<LoadingIndicator message="Carregando tarefas..." />
			) : (
				<DragDropContext onDragEnd={onDragEnd}>
					<Box sx={{ width: '100%', display: 'flex', gap: 1, alignItems: 'flex-start', overflowX: 'auto', pb: 1 }}>
						{columnConfig.map((column) => (
							<Box
								key={column.id}
								sx={{
									display: 'flex',
									flexDirection: 'column',
									width: { xs: '280px', sm: 'calc(33.333% - 6px)' },
									minWidth: { xs: '280px', sm: 'auto' },
									flexShrink: 0,
									backgroundColor: '#f5f5f5',
									borderRadius: '8px',
									boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
								}}
							>
								{/* Header da coluna */}
								<Box
									sx={{
										width: '100%',
										backgroundColor: column.color,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										borderRadius: '8px 8px 0px 0px',
										gap: 1,
										py: 1.5,
										px: 2,
									}}
								>
									<Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.95rem' }}>
										{column.title}
									</Typography>
									<Chip
										label={tasks[column.id as keyof typeof tasks].length}
										size="small"
										sx={{
											backgroundColor: 'rgba(255,255,255,0.25)',
											color: 'white',
											fontWeight: 700,
											minWidth: '28px',
											height: '24px',
										}}
									/>
								</Box>

								{/* Área de cards */}
								<Droppable droppableId={column.id}>
									{(provided, snapshot) => (
										<Box
											ref={provided.innerRef}
											{...provided.droppableProps}
											sx={{
												display: 'flex',
												flexDirection: 'column',
												gap: 1.5,
												p: 1.5,
												backgroundColor: snapshot.isDraggingOver ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
												border: snapshot.isDraggingOver ? '2px dashed rgba(25, 118, 210, 0.3)' : '2px dashed transparent',
												borderRadius: '0 0 12px 12px',
												transition: 'all 0.2s ease',
												minHeight: '100px',
											}}
										>
											{tasks[column.id as keyof typeof tasks].length === 0 && !snapshot.isDraggingOver && (
												<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, opacity: 0.5 }}>
													<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
														Nenhuma tarefa
													</Typography>
												</Box>
											)}
											{tasks[column.id as keyof typeof tasks].map((task, index) => (
												<Draggable key={task.guid} draggableId={task.guid} index={index}>
													{(provided, snapshot) => (
														<Box
															ref={provided.innerRef}
															{...provided.draggableProps}
															{...provided.dragHandleProps}
															sx={{
																cursor: 'grab',
																opacity: snapshot.isDragging ? 0.8 : 1,
																transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
																transition: 'opacity 0.2s ease, transform 0.2s ease',
															}}
														>
															<CardDesdobramento
																desdobramento={task}
																onEdit={() => handleEdit(task.guid)}
																onDelete={() => handleDelete(column.id, task.guid)}
																onViewMore={() => handleViewMore(task.guid)}
																isLoading={loadingCards[task.guid] || false}
															/>
														</Box>
													)}
												</Draggable>
											))}
											{provided.placeholder}
										</Box>
									)}
								</Droppable>
							</Box>
						))}
					</Box>
				</DragDropContext>
			)}

			<TarefaModal
				open={modalAberto}
				onClose={() => setModalAberto(false)}
				desdobramento={tarefaSelecionada}
				modoEdicao={modoEdicao}
				setDesdobramento={modoEdicao ? setTarefaSelecionada : undefined}
				onSave={modoEdicao ? handleSaveTarefa : undefined}
				onEditClick={() => setModoEdicao(true)}
				isNovaTarefa={!tarefaSelecionada?.guid || tarefaSelecionada.guid.trim() === ''}
			/>

			<ConfirmDeleteDialog
				open={deleteConfirmation.isOpen}
				onClose={handleCancelDelete}
				onConfirm={handleDeleteConfirmed}
				title="Confirmar exclusão"
				itemName={deleteConfirmation.taskName}
				isLoading={loadingCards[deleteConfirmation.taskId] || false}
			/>

			<RetornoFinanceiroModal
				open={retornoFinanceiroModalAberto}
				onClose={() => {
					setRetornoFinanceiroModalAberto(false);
					setPlanoFinalizadoGuid('');
				}}
				onSave={handleRetornoFinanceiroSave}
				acaoNome={selectedAcao?.acao}
				retornoFinanceiroInicial=""
			/>
		</Box>
	);
}
